import { useMemo, useState, useRef, useEffect } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { SxProps, Theme } from "@mui/material/styles";

type Option = {
    label: string;
    value: string;
    icon?: string;
};

type MultiSelectDropdownProps = {
    id: string;
    label?: string | null;
    value: string[];
    options: (string | Option)[];
    disabled?: boolean;
    onChange: (value: string[]) => void;
    sx?: SxProps<Theme>;
    dropdownWidth?: number;
    showSearch?: boolean;
    singleSelect?: boolean;
    placeHolder?: string | null;
    showValueLabel?: boolean;
};

const EllipsisText = ({
    text,
    fontSize,
    fontWeight,
}: {
    text: string;
    fontSize: string;
    fontWeight?: number;
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [overflow, setOverflow] = useState(false);

    useEffect(() => {
        if (ref.current) {
            setOverflow(ref.current.scrollWidth > ref.current.clientWidth);
        }
    }, [text]);

    return (
        <Tooltip
            title={overflow ? text : ""}
            disableHoverListener={!overflow}
            slotProps={{ popper: { sx: { zIndex: 99999 } } }}
        >
            <Typography
                ref={ref}
                noWrap
                sx={{ fontSize, fontWeight, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
};

export const MultiSelectDropdown = ({
    id,
    label,
    value,
    options,
    disabled,
    onChange,
    sx,
    dropdownWidth,
    showSearch = false,
    singleSelect = false,
    placeHolder = "",
    showValueLabel = false,
}: MultiSelectDropdownProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const [chipWidth, setChipWidth] = useState(0);
    const [search, setSearch] = useState("");

    const normalizedOptions: Option[] = useMemo(
        () => options.map((o) => (typeof o === "string" ? { label: o, value: o } : o)),
        [options],
    );
    const isAllSelected = value.length === normalizedOptions.length;
    const shouldShowActualValue = value.length === 1 && (singleSelect || showValueLabel);

    useEffect(() => {
        if (buttonRef.current) setChipWidth(buttonRef.current.offsetWidth);
    }, [value, label]);

    const MENU_MIN_WIDTH = Math.max(dropdownWidth ?? 0, chipWidth);

    const filteredOptions = useMemo(() => {
        if (!showSearch || !search.trim()) return normalizedOptions;
        const s = search.toLowerCase();
        return normalizedOptions.filter((opt) => opt.label.toLowerCase().includes(s));
    }, [normalizedOptions, search, showSearch]);

    const sortedFilteredOptions = useMemo(
        () =>
            filteredOptions.slice().sort((a, b) => {
                const aIsAll = a.label.toLowerCase() === "all" || a.value.toLowerCase() === "all";
                const bIsAll = b.label.toLowerCase() === "all" || b.value.toLowerCase() === "all";
                if (aIsAll !== bIsAll) return aIsAll ? -1 : 1;
                return a.label.localeCompare(b.label);
            }),
        [filteredOptions],
    );

    const isAllFilteredSelected =
        filteredOptions.length > 0 && filteredOptions.every((opt) => value.includes(opt.value));

    const someFilteredSelected =
        filteredOptions.some((opt) => value.includes(opt.value)) && !isAllFilteredSelected;

    const handleOpen = () => { if (buttonRef.current) setAnchorEl(buttonRef.current); };
    const handleClose = () => { setAnchorEl(null); setSearch(""); };

    const toggleItem = (val: string) => {
        if (singleSelect) { onChange([val]); handleClose(); return; }
        onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
    };

    const toggleSelectAll = () => {
        if (singleSelect) return;
        const searchActive = showSearch && search.trim().length > 0;
        const targetValues = searchActive
            ? filteredOptions.map((o) => o.value)
            : normalizedOptions.map((o) => o.value);
        const selectedSet = new Set(value);
        const allTargetSelected = targetValues.length > 0 && targetValues.every((v) => selectedSet.has(v));
        if (allTargetSelected) {
            const targetSet = new Set(targetValues);
            onChange(value.filter((v) => !targetSet.has(v)));
        } else {
            const next = new Set(value);
            targetValues.forEach((v) => next.add(v));
            onChange(Array.from(next));
        }
    };

    const checkboxSx = {
        p: 0,
        mr: 0.75,
        "&:not(.Mui-checked)": { color: "var(--text-disabled)" },
    };

    const searchActive = showSearch && search.trim().length > 0;
    const selectAllChecked = searchActive ? isAllFilteredSelected : isAllSelected;
    const selectAllIndeterminate = searchActive ? someFilteredSelected : value.length > 0 && !isAllSelected;
    const selectAllText = searchActive ? "Select All (Visible)" : "Select All";

    return (
        <>
            <Button
                id={id}
                ref={buttonRef}
                disabled={disabled}
                onClick={handleOpen}
                variant="outlined"
                size="small"
                endIcon={
                    <KeyboardArrowDownIcon
                        sx={{ fontSize: "1.1rem", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", ml: "auto", color: "var(--text-tertiary)" }}
                    />
                }
                sx={{
                    width: "100%", maxWidth: "100%", borderRadius: "999px", px: 1.5, py: 0.5, height: 27,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0,
                    textTransform: "none", fontFamily: "Segoe UI, sans-serif", fontSize: "0.8rem",
                    bgcolor: disabled ? "var(--bg-disabled)" : "var(--bg-elevated)",
                    color: disabled ? "var(--text-disabled)" : "var(--text-primary)",
                    borderColor: disabled ? "var(--input-disabled-border)" : "var(--border-default)",
                    overflow: "hidden",
                    "&:hover": { bgcolor: "var(--bg-elevated)", borderColor: disabled ? "var(--input-disabled-border)" : "var(--input-hover-border)" },
                    "&:focus": { borderColor: "var(--input-focus-border)", boxShadow: "var(--input-focus-shadow)" },
                    "&.Mui-disabled": { bgcolor: "var(--bg-disabled)", borderColor: "var(--input-disabled-border)", color: "var(--input-disabled-text)" },
                    ...sx,
                }}
            >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", minWidth: 0, overflow: "hidden" }}>
                    {label && label.trim().length > 0 && (
                        <Typography fontSize="0.8rem" fontWeight={520} noWrap sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                            {label}:
                        </Typography>
                    )}
                    {shouldShowActualValue ? (
                        <>
                            {normalizedOptions.find((o) => o.value === value[0])?.icon && (
                                <img src={normalizedOptions.find((o) => o.value === value[0])?.icon} alt="" style={{ width: 16, height: 16, borderRadius: 2 }} />
                            )}
                            <EllipsisText text={normalizedOptions.find((o) => o.value === value[0])?.label || value[0]} fontSize="0.8rem" />
                        </>
                    ) : (
                        <>
                            {!singleSelect ? (
                                <Typography fontSize="0.8rem" noWrap>
                                    {value.length === 0 || isAllSelected
                                        ? placeHolder === "" || placeHolder === null || isAllSelected ? "ALL" : placeHolder
                                        : `${value.length} of ${normalizedOptions.length}`}
                                </Typography>
                            ) : (
                                <Typography sx={{ color: disabled ? "var(--text-disabled)" : "var(--text-secondary)", fontSize: "0.75rem", fontWeight: 400 }}>
                                    {placeHolder}
                                </Typography>
                            )}
                        </>
                    )}
                </span>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                sx={{ zIndex: 99999 }}
                MenuListProps={{ autoFocusItem: false, onKeyDown: (e) => e.stopPropagation() }}
                slotProps={{
                    paper: {
                        sx: { minWidth: MENU_MIN_WIDTH, maxHeight: 280, mt: 0.5, borderRadius: 1, boxShadow: "var(--dropdown-shadow)", bgcolor: "var(--dropdown-bg)", border: "1px solid var(--dropdown-border)" },
                    },
                    list: {
                        sx: { minWidth: MENU_MIN_WIDTH, maxHeight: 280, mt: 0.5, borderRadius: 1, bgcolor: "var(--dropdown-bg)" },
                    },
                }}
            >
                {/* Optional Search Box */}
                {showSearch && (
                    <Box sx={{ px: 1, py: 0.75, borderBottom: "1px solid var(--dropdown-border)", bgcolor: "var(--dropdown-bg)" }} onKeyDown={(e) => e.stopPropagation()}>
                        <TextField
                            autoFocus size="small" placeholder={`Search ${label!.toLowerCase()}...`}
                            value={search} onChange={(e) => setSearch(e.target.value)} fullWidth
                            onKeyDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                            sx={{
                                "& .MuiOutlinedInput-root": { fontSize: "0.75rem", bgcolor: "var(--dropdown-bg)", color: "var(--text-primary)", "& fieldset": { borderColor: "var(--dropdown-border)" }, "&:hover fieldset": { borderColor: "var(--input-focus-border)" }, "&.Mui-focused fieldset": { borderColor: "var(--input-focus-border)" } },
                                "& .MuiOutlinedInput-input": { color: "var(--text-primary)", "&::placeholder": { color: "var(--text-tertiary)", opacity: 1 } },
                            }}
                        />
                    </Box>
                )}

                {/* Hide Select All if singleSelect is true */}
                {!singleSelect && (
                    <MenuItem dense onClick={toggleSelectAll} sx={{ color: "var(--text-primary)", "&:hover": { bgcolor: "var(--bg-elevated)", color: "var(--text-primary)" } }}>
                        <Checkbox size="small" sx={{ ...checkboxSx, "&.Mui-checked": { color: "var(--status-info)" } }} checked={selectAllChecked} indeterminate={selectAllIndeterminate} />
                        <Typography fontSize="0.75rem">{selectAllText}</Typography>
                    </MenuItem>
                )}

                {sortedFilteredOptions.map((opt) => (
                    <MenuItem
                        key={opt.value} dense onClick={() => toggleItem(opt.value)}
                        sx={{
                            display: "flex", alignItems: "center", gap: 0.5, color: "var(--text-primary)",
                            backgroundColor: singleSelect && value.includes(opt.value) ? "var(--dropdown-item-selected-bg)" : "var(--dropdown-bg)",
                            "&:hover": { backgroundColor: "var(--dropdown-item-hover-bg)", color: "var(--text-primary)" },
                        }}
                    >
                        {!singleSelect && (
                            <Checkbox size="small" sx={{ ...checkboxSx, "&.Mui-checked": { color: "var(--status-info)" } }} checked={value.includes(opt.value)} />
                        )}
                        {opt.icon && <img src={opt.icon} alt="" style={{ width: 16, height: 16, marginRight: 4 }} />}
                        <EllipsisText text={opt.label} fontSize="0.75rem" fontWeight={400} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};